import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { OMP_HISTORY_LIMIT, OMP_TEXT_GROUP_MS, OmpHistoryLabel, OmpHistoryStep } from '../_models';

/**
 * Client-side undo/redo for the Order Management Portal editor. Never calls the API.
 * Steps hold the editor state serialized, so no-op and dirty checks are string compares.
 * `steps[0]` is the oldest reachable state and `cursor` the current one.
 * While a group is open (slider or color drag, a burst of typing) records update one step.
 */
@Injectable()
export class OmpEditorHistoryService {
  private steps: OmpHistoryStep[] = [];
  private cursor = -1;
  private groupOpen = false;
  private groupStep = -1;
  private textTimer: any = null;
  private changesSubject = new Subject<void>();

  readonly changes$ = this.changesSubject.asObservable();

  get ready(): boolean {
    return this.cursor >= 0;
  }

  get canUndo(): boolean {
    return this.cursor > 0;
  }

  get canRedo(): boolean {
    return this.cursor >= 0 && this.cursor < this.steps.length - 1;
  }

  get undoLabel(): string {
    let label = '';
    if (this.canUndo) {
      label = this.steps[this.cursor].label;
    }
    return label;
  }

  get redoLabel(): string {
    let label = '';
    if (this.canRedo) {
      label = this.steps[this.cursor + 1].label;
    }
    return label;
  }

  get current(): OmpHistoryStep {
    let step = null;
    if (this.ready) {
      step = this.steps[this.cursor];
    }
    return step;
  }

  reset(styles: string, sections: string): void {
    this.closeGroup();
    this.steps = [{ label: OmpHistoryLabel.LastSaved, styles: styles, sections: sections }];
    this.cursor = 0;
    this.changesSubject.next();
  }

  clear(): void {
    this.closeGroup();
    this.steps = [];
    this.cursor = -1;
    this.changesSubject.next();
  }

  record(styles: string, sections: string, label: string): void {
    const present = this.current;
    if (present && (present.styles !== styles || present.sections !== sections)) {
      if (this.groupOpen && this.groupStep === this.cursor) {
        present.styles = styles;
        present.sections = sections;
      } else {
        this.steps = this.steps.slice(0, this.cursor + 1);
        this.steps.push({ label: label, styles: styles, sections: sections });
        if (this.steps.length > OMP_HISTORY_LIMIT + 1) {
          this.steps.shift();
        }
        this.cursor = this.steps.length - 1;
        this.groupStep = this.groupOpen ? this.cursor : -1;
      }
      this.changesSubject.next();
    }
  }

  beginGroup(): void {
    this.clearTextTimer();
    this.groupOpen = true;
    this.groupStep = -1;
  }

  endGroup(): void {
    this.closeGroup();
  }

  touchTextGroup(): void {
    if (!this.groupOpen) {
      this.beginGroup();
    }
    this.clearTextTimer();
    this.textTimer = setTimeout(() => {
      this.textTimer = null;
      this.closeGroup();
    }, OMP_TEXT_GROUP_MS);
  }

  undo(): OmpHistoryStep {
    return this.jumpTo(this.cursor - 1);
  }

  redo(): OmpHistoryStep {
    return this.jumpTo(this.cursor + 1);
  }

  jumpTo(index: number): OmpHistoryStep {
    let step = null;
    this.closeGroup();
    if (this.ready && index >= 0 && index < this.steps.length && index !== this.cursor) {
      this.cursor = index;
      step = this.steps[index];
      this.changesSubject.next();
    }
    return step;
  }

  private closeGroup(): void {
    this.clearTextTimer();
    this.groupOpen = false;
    this.groupStep = -1;
  }

  private clearTextTimer(): void {
    if (this.textTimer) {
      clearTimeout(this.textTimer);
      this.textTimer = null;
    }
  }
}
